import React from 'react';
import { action, computed, observable } from 'mobx';
import { observer } from 'mobx-react';
import { inject } from '../../utils';
import { createPortal } from 'react-dom';
import { withTheme } from 'styled-components';
import { utils } from '@kurtosys/ksys-app-template';

// Utils
import { ThemeableComponent } from '../../utils/theme/ThemeableComponent';
import { ModalHelper } from '../../utils/accessibility/ModalHelper';

// Reused Components
import ClickLayer from '../ClickLayer';
import Translate, { translate } from '../Translate';

// Modal Models
import { ModalButtonProps, ModalProps, ModalThemeKeys } from './Models';

// Modal Styled Components
import ModalWrapper from './StyledComponents/ModalWrapper';
import ModalTitleBar from './StyledComponents/ModalTitleBar';
import ModalTitle from './StyledComponents/ModalTitle';
import ModalCloseButton from './StyledComponents/ModalCloseButton';
import ModalCloseIcon from './StyledComponents/ModalCloseIcon';
import ModalHead from './StyledComponents/ModalHead';
import ModalBody from './StyledComponents/ModalBody';
import ModalFooter from './StyledComponents/ModalFooter';
import ModalActions from './StyledComponents/ModalActions';
import ModalAction from './StyledComponents/ModalAction';
import ModalActionClose from './StyledComponents/ModalActionClose';
import ModalExpanderButton from './StyledComponents/ModalExpanderButton';
import ModalExpanderIcon from './StyledComponents/ModalExpanderIcon';

@inject('translationStore', 'dataStore')
@observer
export class Modal extends ThemeableComponent<ModalProps, {}, ModalThemeKeys> {
    DEFAULT_THEME_KEYS: ModalThemeKeys = {
        action: 'ModalActionButton',
        actions: 'ModalActions',
        body: 'ModalBodyWrapper',
        closeButton: 'ModalCloseButton',
        closeIcon: 'ModalCloseIcon',
        expanderButton: 'ModalExpandButton',
        expanderIcon: 'ModalExpandIcon',
        footer: 'ModalFooterWrapper',
        header: 'ModalHeaderWrapper',
        title: 'ModalTitle',
        titleBar: 'ModalTitleBar',
        wrapper: 'ModalWrapper',
    };

    @observable
    showModal = false;

    @computed
    get isOpen() {
        const { isOpen } = this.props;
        if (isOpen !== undefined) {
            return isOpen;
        }
        return this.showModal;
    }

    titleId: string;
    // Used to provide an accessible description for the modal when available.
    headerId: string;
    lastFocus: Element;

    constructor(props: ModalProps) {
        super(props);
        this.titleId = `modal-${
            props.modalId
        }-title-${utils.guid.generateGuid()}`;
        this.headerId = `modal-${
            props.modalId
        }-header-${utils.guid.generateGuid()}`;
        if (props.isOpen !== undefined) {
            this.handleIsOpenInitialization(props.isOpen);
        }
    }

    componentDidUpdate(prevProps: ModalProps) {
        if (this.props.isOpen !== prevProps.isOpen) {
            this.handleIsOpenInitialization(this.props.isOpen);
        }
    }

    handleIsOpenInitialization(isOpen: boolean) {
        isOpen ? this.handleOpen() : this.handleClose();
    }

    /**
     * Modal Title which is reference by screen readers when open the modal.
     * Additionally the close button is display here
     *
     * @readonly
     */
    @computed
    get titleBar() {
        const { title } = this.props;
        return (
            <ModalTitleBar {...this.getTheme('titleBar')}>
                <ModalTitle id={this.titleId} {...this.getTheme('title')}>
                    <Translate>{title}</Translate>
                </ModalTitle>
                {this.titleBarCloseButton}
            </ModalTitleBar>
        );
    }

    @computed
    get titleBarCloseButton() {
        const { title, closeButtonProps } = this.props;
        const buttonProps = this.getModalButtonProps(closeButtonProps, {
            label: null,
            iconFileName: 'CloseIcon',
            iconPosition: 'right',
            disable: false,
        });
        if (buttonProps.disable) {
            return null;
        }
        const CloseIcon = (
            <ModalCloseIcon
                fileName={buttonProps.iconFileName}
                aria-hidden={true}
                {...this.getTheme('closeIcon')}
            />
        );
        return (
            <ModalCloseButton
                onClick={e => this.handleClose()}
                {...this.getTheme('closeButton')}
                aria-label={translate(
                    this.props.translationStore,
                    buttonProps.label || `Close ${title} dialog`
                )}
            >
                {buttonProps.iconPosition === 'left' && CloseIcon}
                {buttonProps.label && (
                    <Translate>{buttonProps.label}</Translate>
                )}
                {buttonProps.iconPosition === 'right' && CloseIcon}
            </ModalCloseButton>
        );
    }

    @computed
    get headerContent() {
        const { headerContent } = this.props;
        return (
            headerContent && (
                <ModalHead id={this.headerId} {...this.getTheme('header')}>
                    {headerContent}
                </ModalHead>
            )
        );
    }

    @computed
    get bodyContent() {
        const { children, bodyContent } = this.props;
        return (
            <ModalBody {...this.getTheme('body')}>
                {bodyContent || children}
            </ModalBody>
        );
    }

    @computed
    get footerContent() {
        const { footerContent } = this.props;
        return (
            footerContent && (
                <ModalFooter {...this.getTheme('footer')}>
                    {footerContent}
                </ModalFooter>
            )
        );
    }

    /**
     * Typical modal action buttons that appear below the footer content
     *
     * @readonly
     */
    @computed
    get actions() {
        const { actions } = this.props;
        if (actions && actions.length > 0) {
            return (
                <ModalActions {...this.getTheme('actions')}>
                    {actions.map((modalAction, modalActionIndex) => {
                        const ModalActionButton =
                            modalAction.type === 'custom'
                                ? ModalAction
                                : ModalActionClose;
                        return (
                            <ModalActionButton
                                key={`action-${modalActionIndex}-${modalAction.id}`}
                                onClick={e => {
                                    switch (modalAction.type) {
                                        case 'close':
                                            this.handleClose();
                                            break;
                                        case 'closeAndCustom':
                                            this.handleClose();
                                        default:
                                            if (modalAction.onAction) {
                                                modalAction.onAction(
                                                    modalAction.id
                                                );
                                            }
                                            break;
                                    }
                                }}
                                {...this.getTheme('action')}
                            >
                                <Translate>{modalAction.label}</Translate>
                            </ModalActionButton>
                        );
                    })}
                </ModalActions>
            );
        }
        return null;
    }

    /**
     * The button responsible for opening the modal
     *
     * @readonly
     */
    @computed
    get expanderButton() {
        const { title, expanderButtonProps } = this.props;
        const buttonProps = this.getModalButtonProps(expanderButtonProps, {
            label: `Expand ${title} dialog`,
            iconFileName: !this.isOpen
                ? 'DropdownChevronIcon'
                : 'DropdownChevronIconExpanded',
            iconPosition: 'right',
            disable: false,
        });
        if (buttonProps.disable) {
            return null;
        }
        const ExpandIcon = (
            <ModalExpanderIcon
                fileName={buttonProps.iconFileName}
                aria-hidden="true"
                {...this.getTheme('expanderIcon')}
            />
        );
        return (
            <ModalExpanderButton
                onClick={e => this.handleOpen()}
                {...this.getTheme('expanderButton')}
            >
                {buttonProps.iconPosition === 'left' && ExpandIcon}
                <Translate>{buttonProps.label}</Translate>
                {buttonProps.iconPosition === 'right' && ExpandIcon}
            </ModalExpanderButton>
        );
    }

    /**
     * Used by the static ModalHelper class
     *
     * @readonly
     */
    @computed
    get modalNode() {
        const { modalId, dataStore } = this.props;
        if (dataStore && dataStore.shadowRoot) {
            return dataStore.shadowRoot.getElementById(modalId);
        }
    }

    @action
    handleClose = () => {
        const { onClose } = this.props;
        this.showModal = false;
        ModalHelper.removeActiveModal(this);
        if (onClose) {
            onClose();
        }
    };
    @action
    handleOpen = () => {
        this.showModal = true;
        ModalHelper.addActiveModal(this);
    };

    getModalButtonProps(
        props: ModalButtonProps,
        defaultProps: Required<ModalButtonProps>
    ): Required<ModalButtonProps> {
        if (props) {
            return { ...defaultProps, ...props };
        }
        return defaultProps;
    }

    render() {
        return (
            <>
                {this.isOpen &&
                    createPortal(
                        <>
                            <ModalWrapper
                                role="dialog"
                                aria-modal="true"
                                aria-labelledby={this.titleId}
                                aria-describedby={
                                    this.headerContent && this.headerId
                                }
                                {...this.getTheme('wrapper')}
                            >
                                {this.titleBar}
                                {this.headerContent}
                                {this.bodyContent}
                                {this.footerContent}
                                {this.actions}
                            </ModalWrapper>
                            <ClickLayer
                                onClick={() => this.handleClose()}
                                obscure={true}
                            />
                        </>,
                        this.modalNode
                    )}
                {this.expanderButton}
            </>
        );
    }
}

export default withTheme(Modal);



// WEBPACK FOOTER //
// ./src/Components/Modal/index.tsx